/**
 * LinkedIn Insight Tag — consent-gated integration
 * ─────────────────────────────────────────────────
 * Partner ID: 10679809 (provided by Danny Clans LinkedIn Campaign Manager).
 *
 * WHY THIS EXISTS
 *   LinkedIn's default install snippet fires `insight.min.js` inline in
 *   <head>, which unconditionally sets a first-party cookie and pings
 *   `px.ads.linkedin.com` before the user has agreed to marketing
 *   cookies — a GDPR / ePrivacy violation for EU/UK visitors and one of
 *   the specific reasons the site was flagged in the previous audit.
 *
 *   This wrapper defers ALL LinkedIn Insight Tag activity until:
 *     1. `consent.marketing === true` in localStorage-backed state, AND
 *     2. we're running in a real browser (not the react-snap prerender
 *        headless pass — which uses UA "ReactSnap").
 *
 *   Layout mirrors `metaPixel.js` so the two trackers boot from a single
 *   consent subscription in `App.js` and behave identically.
 *
 *   Zero PII in this module — LinkedIn's SDK owns all data flow; we
 *   only expose the queue and the conversion helper.
 */

import { hasMarketingConsent, subscribeConsent } from "./consent";
import { devLog } from "./devLog";

// Danny Clans LinkedIn Campaign Manager partner ID.
// Kept as a module-level const so it's constant-folded at build time
// and clearly greppable during audits.
const LINKEDIN_PARTNER_ID = "10679809";
const INSIGHT_SDK_URL = "https://snap.licdn.com/li.lms-analytics/insight.min.js";

let _insightInitialized = false;

/**
 * Detect the react-snap prerender pass so we never bake a LinkedIn
 * PageView into the static HTML (which would then fire again on real
 * hydration, double-counting every prerendered route).
 */
const isPrerenderPass = () =>
  typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "");

/**
 * Idempotent SDK installer. Attaches `insight.min.js` and configures the
 * global `lintrk` queue exactly as LinkedIn's official snippet does —
 * except only ever executed after `hasMarketingConsent()` is true.
 */
const _installInsightStub = () => {
  if (typeof window === "undefined") return;
  if (window._linkedin_installed) return;

  // Partner ID registration — matches the reference snippet.
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
    window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  }

  // Lightweight queue stub so `window.lintrk(...)` calls are safe
  // before the SDK loads.
  if (typeof window.lintrk !== "function") {
    window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
    window.lintrk.q = [];
  }

  // Async-inject the LinkedIn SDK script. Insertion strategy matches
  // LinkedIn's official snippet: prepend before the first <script>
  // element so it's evaluated ASAP without blocking hydration.
  const firstScript = document.getElementsByTagName("script")[0];
  const s = document.createElement("script");
  s.type = "text/javascript";
  s.async = true;
  s.src = INSIGHT_SDK_URL;
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(s, firstScript);
  } else {
    document.head.appendChild(s);
  }

  window._linkedin_installed = true;
};

/**
 * Public entrypoint. Safe to call multiple times — no-ops after the
 * first successful init. Only fires after marketing consent is
 * granted, so calling it before consent is a silent no-op.
 */
export const initLinkedInInsight = () => {
  if (_insightInitialized) return;
  if (isPrerenderPass()) return;           // never poison the static HTML
  if (!hasMarketingConsent()) return;       // hard guard for defence in depth

  try {
    _installInsightStub();
    _insightInitialized = true;
    devLog.log("linkedInInsight: initialised post-consent");
  } catch (err) {
    devLog.warn("linkedInInsight: init failed", err);
  }
};

/**
 * Fire a LinkedIn conversion event.
 *
 * Usage (from a contact-form success handler):
 *     trackLinkedInConversion();                  // generic Lead conversion
 *     trackLinkedInConversion("YOUR_CONV_ID");    // specific Campaign Manager conversion
 *
 * When you set up conversion IDs in LinkedIn Campaign Manager
 * (Insight Tag → Conversions → Create Conversion → server-side
 * tracking), paste the numeric conversion_id and it will be sent to
 * LinkedIn as `lintrk('track', { conversion_id: <id> })`.
 *
 * Also safe to call before consent — it drops the event with a
 * dev-log note rather than throwing.
 */
export const trackLinkedInConversion = (conversionId = null) => {
  if (!hasMarketingConsent()) {
    devLog.log("linkedInInsight: skipped conversion — no marketing consent");
    return;
  }
  if (isPrerenderPass()) return;
  if (!_insightInitialized) initLinkedInInsight();

  try {
    if (typeof window.lintrk === "function") {
      if (conversionId) {
        window.lintrk("track", { conversion_id: conversionId });
      } else {
        // Untyped Lead conversion — LinkedIn will attribute it to the
        // page URL, campaign, and audience via the base pixel.
        window.lintrk("track");
      }
      devLog.log(`linkedInInsight: conversion sent${conversionId ? ` (id=${conversionId})` : ""}`);
    }
  } catch (err) {
    devLog.warn("linkedInInsight: conversion failed", err);
  }
};

/**
 * Bootstrap called once from `App.js`. If the visitor has already
 * granted marketing consent on a previous visit, we init immediately.
 * Either way, we subscribe to future consent changes so a Reject →
 * Accept switch inside Cookie preferences installs the tag on the fly.
 */
export const bootstrapLinkedInWithConsent = () => {
  if (isPrerenderPass()) return;
  if (hasMarketingConsent()) initLinkedInInsight();
  subscribeConsent((c) => {
    if (c && c.marketing) initLinkedInInsight();
  });
};
